import { useLocation, useMatch } from "react-router-dom";
import { useMemo } from "react";

import { appURLs } from "../../lib/appRoutes/appUrls";
import { useSafeNavigate, type SafeNavigate } from "./useSafeNavigate";

export interface IProtocolsRouter {
  goBack: () => void;
  goToProtocolsList: () => void;
  goToProtocolsEditPage: (
    subpath?: string,
    protocolId?: string,
    step?: string,
    isUpdateProtocolSuccess?: boolean | undefined,
    goToSummary?: boolean | undefined
  ) => void;
  goToProtocolsCreatePage: (
    subpath?: string,
    step?: string,
    isCreateProtocolSuccess?: boolean | undefined
  ) => void;
}

/**
 * to get the router for Protocols module
 * @returns An object containing the Protocols router, current location, workspaceId, orgId, farmId, protocolId, and navigate function.
 */
export const useProtocolsRouter = (): {
  router: IProtocolsRouter;
  location: ReturnType<typeof useLocation>;
  workspaceId?: string;
  orgId?: string;
  farmId?: string;
  protocolId?: string;
  navigate: SafeNavigate;
} => {
  const navigate = useSafeNavigate();
  const location = useLocation();

  const match = useMatch(
    `${appURLs.PROTOCOLS}/workspace/:workspaceId/org/:orgId/farm/:farmId/*`
  );
  const { workspaceId, orgId, farmId }: Record<string, string | undefined> =
    match?.params ?? {};

  const { protocolId }: Record<string, string | undefined> =
    useMatch(
      `${appURLs.PROTOCOLS}/workspace/:workspaceId/org/:orgId/farm/:farmId/protocol/:protocolId/*`
    )?.params ?? {};

  const protocolsListingRoute = `${appURLs.PROTOCOLS}/workspace/${
    workspaceId as string
  }/org/${orgId as string}/farm/${farmId as string}`;
  const editProtocolRoute = `${appURLs.PROTOCOLS}/workspace/${
    workspaceId as string
  }/org/${orgId as string}/farm/${farmId as string}`;

  const getSubPathValue = (subpath: string | undefined): string =>
    subpath ? `?${subpath}` : "";

  const router: IProtocolsRouter = useMemo(
    () => ({
      goBack: () => {
        navigate(-1);
      },
      goToProtocolsEditPage: (
        subpath?: string,
        protocolId?: string,
        step?: string,
        isUpdateProtocolSuccess?: boolean | undefined,
        goToSummary?: boolean | undefined
      ) => {
        const path = `${editProtocolRoute}/protocol/${protocolId ?? ""}/${
          step ?? ""
        }${getSubPathValue(subpath)}`;
        navigate(path, {
          state: {
            isUpdateProtocolSuccess,
            goToSummary,
          },
        });
      },
      goToProtocolsCreatePage: (
        subpath?: string,
        step?: string,
        isCreateProtocolSuccess?: boolean | undefined
      ) => {
        const path = `${protocolsListingRoute}/create/${
          step ?? ""
        }${getSubPathValue(subpath)}`;
        navigate(path, {
          state: {
            isCreateProtocolSuccess,
          },
        });
      },
      goToProtocolsList: () => {
        navigate(protocolsListingRoute);
      },
    }),
    [protocolsListingRoute, editProtocolRoute, navigate, protocolId]
  );

  return { router, location, workspaceId, orgId, farmId, protocolId, navigate };
};
